import { uniq } from "lodash";
import { DEFAULT_SCHEDULING_TIMEZONE } from "@hightouch/lib/customer-data/decision-engine/types";
import ianaTimeZones from "@hightouch/lib/customer-data/decision-engine/iana-time-zones.json";
import {
  isRegisteredPreviewSend,
  type DecisionEngineDestinationDefinition,
} from "./definition";
import {
  generateDataExtensionKey,
  getMappingForUserColumn,
  getMappingForVariable,
  hightouchPayloadMapping,
  PREVIEW_TRACE_ID_VARIABLE,
} from "./sync-config-helpers";

export const sfmcTriggerDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "sfmc",
  category: "trigger",
  generateSyncConfig: ({
    producer,
    channelType,
    settings,
    campaignId,
    isPreview,
    sendToHightouchUser,
    previewRecipientMode,
    messageVariables = [],
    messageTags,
  }) => {
    const registeredPreview = isRegisteredPreviewSend({
      isPreview,
      previewRecipientMode,
    });
    if (channelType === "push") {
      return {
        type: "triggerPush",
        templateId: campaignId,
        ...(settings.businessUnit
          ? { businessUnit: settings.businessUnit }
          : {}),
        subscriberKey: { type: "standard", from: "external_id" },
        // A journey sync has no message variables; the push template carries the content.
        mappings:
          producer === "journey"
            ? []
            : [
                getMappingForVariable("title", "message.title"),
                getMappingForVariable("subtitle", "message.subtitle"),
                getMappingForVariable("body", "message.alert"),
              ],
      };
    }
    return {
      type: "trigger",
      ...(settings.businessUnit ? { businessUnit: settings.businessUnit } : {}),
      triggeredSendDefinitionId: { type: "standard", from: "campaign_id" },
      ...(registeredPreview
        ? {
            email: { type: "standard", from: "external_id" },
            subscriberKey: { type: "standard", from: "external_id" },
          }
        : isPreview && sendToHightouchUser
          ? {
              email: { type: "standard", from: "external_id" },
              subscriberKey: {
                type: "static",
                value: "hightouch_preview_user",
                valueType: "STRING",
              },
            }
          : {
              subscriberKey: { type: "standard", from: "external_id" },
            }),
      mappings: [
        hightouchPayloadMapping({ producer, messageTags }),
        // AMPscript can't traverse the hightouch JSON attribute, so the
        // registered-mode snippet reads a flat triggered-send attribute:
        // %%=v(AttributeValue("AidPreviewTraceId"))=%%
        ...(registeredPreview
          ? [
              getMappingForVariable(
                PREVIEW_TRACE_ID_VARIABLE,
                PREVIEW_TRACE_ID_VARIABLE,
              ),
            ]
          : []),
        // Iterable (and most ESPs) only need hightouchMappingWithTags above:
        // templates read bandit values at hightouch.recommendation.variables.<name>.
        // SFMC still sends that nested `hightouch` attribute, but AMPscript
        // AttributeValue() cannot traverse JSON — so each message variable must
        // also ship as a flat SOAP Attribute under the same name. Dynamic-content
        // AID AMPscript switches on AttributeValue("slot_*"); without these
        // mappings every slot falls through to the default branch. (Journeys
        // already flat-mapped messageVariables; trigger omitted them until now.)
        ...uniq(messageVariables.map((variable) => variable.name)).map((name) =>
          getMappingForVariable(name, name),
        ),
        ...(settings.userColumns ?? []).map((column) =>
          getMappingForUserColumn(
            column.column.toLowerCase(),
            column.overridePropertyName ?? column.column,
          ),
        ),
      ],
    };
  },
};

export const sfmcJourneyDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "sfmc",
  category: "event",
  generateSyncConfig: ({
    producer,
    settings,
    resourceId,
    messageVariables = [],
    messageTags,
  }) => ({
    type: "journeys",
    journeyKey: resourceId || { type: "standard", from: "campaign_id" },
    ...(settings.businessUnit ? { businessUnit: settings.businessUnit } : {}),
    externalIdMapping: { from: "external_id", to: "contactKey" },
    mappings: [
      hightouchPayloadMapping({ producer, messageTags }),
      { from: "external_id", to: "contactKey" },
      ...uniq(messageVariables.map((variable) => variable.name)).map((name) =>
        getMappingForVariable(name, name),
      ),
      ...(settings.userColumns ?? []).map((column) =>
        getMappingForUserColumn(
          column.column.toLowerCase(),
          column.overridePropertyName ?? column.column,
        ),
      ),
    ],
    deleteMode: null,
    useAsyncEndpoints: true,
  }),
  resourceLifecycle: {
    requiresResourceUpdateOnVariableChange: true,
  },
};

export const sfmcDataExtensionsDefinition: DecisionEngineDestinationDefinition =
  {
    destinationType: "sfmc",
    category: "batch",
    producers: ["aid"],
    generateSyncConfig: ({
      producer,
      scopeId,
      unitId,
      channelType,
      settings,
      isPreview,
      messageVariables = [],
      messageTags,
    }) => {
      const dataExtensionKey = generateDataExtensionKey(unitId, scopeId);
      return {
        type: "dataExtensions",
        ...(settings.businessUnit
          ? { businessUnit: settings.businessUnit }
          : {}),
        mode: settings.syncMode,
        object: dataExtensionKey,
        externalIdMapping: { to: "subscriberKey", from: "external_id" },
        mappings: [
          hightouchPayloadMapping({ producer, messageTags }),
          { type: "standard", from: "id", to: "interactionId" },
          { type: "standard", from: "send_at", to: "sendAt" },
          { type: "standard", from: "message_id", to: "messageId" },
          {
            type: "static",
            to: "channel",
            value: channelType,
            valueType: "STRING",
          },
          ...(isPreview
            ? [
                {
                  type: "static",
                  to: "isTest",
                  value: "true",
                  valueType: "BOOLEAN",
                },
              ]
            : []),
          { type: "standard", from: "campaign_id", to: "templateId" },
          ...uniq(messageVariables.map((variable) => variable.name)).map(
            (name) => getMappingForVariable(name, name),
          ),
          ...(settings.userColumns ?? []).map((column) => ({
            ...getMappingForUserColumn(
              column.column.toLowerCase(),
              column.overridePropertyName ?? column.column,
            ),
          })),
        ],
        ...(settings.syncMode === "mirror" ? { allowClearData: true } : {}),
      };
    },
    // No `modes` here even though the rETL sync honors mode: this definition
    // already emits `channelConfig.syncMode` as the mode, so the channel's
    // existing syncMode field owns it — a second knob would fight it. 10M
    // mirrors the rETL form max; SOAP deletes clamp to 70k at runtime.
    syncTuning: ({ channelConfig }) => ({
      batchSize: {
        configKey: "rowsPerBatch",
        defaultValue: "100,000 / 70,000",
        max: 10_000_000,
        label: "Processing batch size",
        description:
          "Defaults to 100,000 rows per file-upload batch and 70,000 per delete request. Deletes are capped at 70,000 rows. File batches may be combined into one import.",
      },
      ...(channelConfig.syncMode === "mirror"
        ? { notice: "Mirror syncs import the full dataset in one file." }
        : {}),
      // Mirror takes the file-upload path unconditionally, so there is no choice left.
      ...(channelConfig.syncMode === "mirror" ? {} : { useFtp: true }),
    }),
    syncStrategy: {
      supportsChannelLevelSync: (channelConfig) =>
        !channelConfig.useLegacyDataExtension,
      getSyncSchedule: ({ channelConfig, schedulingTimezone }) => {
        if (channelConfig.syncMode === "mirror") {
          const timezoneOffset =
            ianaTimeZones[schedulingTimezone ?? DEFAULT_SCHEDULING_TIMEZONE];

          if (!timezoneOffset) {
            throw new Error(`Invalid timezone: ${schedulingTimezone}`);
          }

          const [hours, minutes] = timezoneOffset.split(":").map(Number);
          const offsetHours = hours + minutes / 60;
          const utcHour = (23 - offsetHours + 24) % 24;

          return {
            type: "cron",
            schedule: { expression: `0 ${utcHour} * * *` },
          };
        }
        return { type: "cron", schedule: { expression: "0 * * * *" } };
      },
    },
    resourceLifecycle: {
      requiresResourceUpdateOnVariableChange: (channelConfig) =>
        !channelConfig.useLegacyDataExtension,
      usesChannelLevelResourceUpdate: (channelConfig) =>
        !channelConfig.useLegacyDataExtension,
    },
  };
